import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/forms/phone-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { myContextQuery, programsAuthedQuery } from "@/lib/queries";
import { T1D_RELATIONSHIPS } from "@/lib/t1d-relationships";
import { SHIRT_SIZE_OPTIONS } from "@/lib/claim-fields";
import { US_STATES, normalizeUsState } from "@/lib/us-states";
import { saveMyProfile } from "@/lib/volunteer.functions";
import { buildSignatureFromProfile } from "@/lib/email-signature";
import { aliasAddress } from "@/lib/volunteer-alias";
import { reportHandledSync } from "@/lib/observability";

export const Route = createFileRoute("/_authenticated/profile")({
  head: () => ({
    meta: [
      { title: "My Profile | Warrior Volunteer" },
      { name: "description", content: "My Profile in the T1D Warriors volunteer workspace." },
      { property: "og:title", content: "My Profile | Warrior Volunteer" },
      { property: "og:description", content: "My Profile in the T1D Warriors volunteer workspace." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ProfilePage,
});

const TRACKS = [
  { value: "remote", label: "Remote outreach" },
  { value: "onsite", label: "On-site crew" },
] as const;

type TrackValue = (typeof TRACKS)[number]["value"];

function consentSummary(
  granted?: boolean | null,
  at?: string | null,
  source?: string | null,
) {
  if (!granted) return "Not opted in";
  const when = at ? new Date(at).toLocaleString() : "date unknown";
  return `Opted in ${when}${source ? ` via ${source}` : ""}`;
}


function ProfilePage() {
  const { data: me } = useQuery(myContextQuery);
  const { data: programs } = useQuery(programsAuthedQuery);
  const save = useServerFn(saveMyProfile);
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string[] | null>(null);
  const [tracks, setTracks] = useState<TrackValue[] | null>(null);
  const [signature, setSignature] = useState("");
  const [signatureTitle, setSignatureTitle] = useState("");
  const [includeLogo, setIncludeLogo] = useState(false);

  const profile = me?.profile;

  const [state, setState] = useState(normalizeUsState(profile?.state));
  useEffect(() => {
    setState(normalizeUsState(profile?.state));
  }, [profile?.state]);

  useEffect(() => {
    setSignature(profile?.email_signature ?? "");
    setSignatureTitle(profile?.signature_title ?? "");
    setIncludeLogo(Boolean(profile?.signature_include_logo));
  }, [profile?.email_signature, profile?.signature_title, profile?.signature_include_logo]);

  // Signatures always show the T1D Warriors outreach address, never the
  // personal login email the volunteer signed up with.
  const outreachAddress = profile?.alias_local_part
    ? aliasAddress(profile.alias_local_part)
    : null;

  const prefillSignature = () =>
    setSignature(
      buildSignatureFromProfile({
        full_name: profile?.full_name,
        signature_title: signatureTitle,
        phone: profile?.phone,
        email: outreachAddress,
        city: profile?.city,
        state: profile?.state,
      }),
    );

  const chosenPrograms = selected ?? (profile?.programs_of_interest ?? []);
  const savedTrack = profile?.volunteer_track ?? "remote";
  const chosenTracks =
    tracks ??
    ((savedTrack === "both" ? ["remote", "onsite"] : [savedTrack]) as TrackValue[]);
  const allProgramsSelected =
    (programs ?? []).length > 0 && chosenPrograms.length === (programs ?? []).length;

  const toggleTrack = (value: TrackValue) =>
    setTracks((prev) => {
      const base = prev ?? chosenTracks;
      return base.includes(value) ? base.filter((t) => t !== value) : [...base, value];
    });

  const trackValue =
    chosenTracks.includes("remote") && chosenTracks.includes("onsite")
      ? "both"
      : chosenTracks.includes("onsite")
        ? "onsite"
        : "remote";

  const nameParts = (profile?.full_name ?? "").trim().split(/\s+/).filter(Boolean);
  const firstName = nameParts[0] ?? "";
  const lastName = nameParts.slice(1).join(" ");

  const toggleProgram = (name: string) =>
    setSelected((prev) => {
      const base = prev ?? (profile?.programs_of_interest ?? []);
      return base.includes(name) ? base.filter((p) => p !== name) : [...base, name];
    });


  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    try {
      await save({
        data: {
          full_name: [String(form.get("first_name") ?? "").trim(), String(form.get("last_name") ?? "").trim()]
            .filter(Boolean)
            .join(" "),
          phone: String(form.get("phone") ?? ""),
          birthday: String(form.get("birthday") ?? ""),
          city: String(form.get("city") ?? ""),
          state: String(form.get("state") ?? "") || (profile?.state ?? ""),
          t1d_connection: String(form.get("t1d_connection") ?? ""),
          shirt_size: String(form.get("shirt_size") ?? ""),
          volunteer_track: trackValue,
          availability: String(form.get("availability") ?? ""),
          why_help: String(form.get("why_help") ?? ""),
          email_signature: signature,
          signature_title: signatureTitle,
          signature_include_logo: includeLogo,
          programs_of_interest: chosenPrograms,
          skills: String(form.get("skills") ?? "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        },
      });
      toast.success("Profile saved.");
      queryClient.invalidateQueries({ queryKey: ["me"] });
    } catch (error) {
      reportHandledSync(error, { feature: "profile", action: "handleSubmit" });
      toast.error(error instanceof Error ? error.message : "Could not save your profile.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell eyebrow="Account" title="My profile">
      <form onSubmit={handleSubmit} className="panel max-w-2xl space-y-6 p-7">
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="first_name">First name</Label>
            <Input id="first_name" name="first_name" defaultValue={firstName} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="last_name">Last name</Label>
            <Input id="last_name" name="last_name" defaultValue={lastName} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={profile?.email ?? me?.email ?? ""} disabled />
          </div>
          <div className="space-y-2">
            <Label htmlFor="alias_email">Your outreach address</Label>
            <Input
              id="alias_email"
              value={
                profile?.alias_local_part
                  ? `${profile.alias_local_part}@volunteer.t1dw.org`
                  : "Assigned when your volunteer account is approved"
              }
              disabled
            />
            <p className="t-meta">
              Outreach you send from the app goes out from this address, and replies land on the
              contact timeline.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <PhoneInput id="phone" name="phone" defaultValue={profile?.phone ?? ""} />
            <p className="t-meta">Used for shift texts, so a mobile number works best.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="birthday">Birthday</Label>
            <Input
              id="birthday"
              name="birthday"
              type="date"
              defaultValue={(profile as { birthday?: string | null } | null)?.birthday ?? ""}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input id="city" name="city" defaultValue={profile?.city ?? ""} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <select
              id="state"
              name="state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="h-11 w-full rounded-sm border border-border bg-background px-3 text-[0.9375rem] text-foreground"
            >
              {US_STATES.map((s) => (
                <option key={s.value} value={s.value} disabled={s.value === ""}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-3">
          <Label>How you volunteer</Label>
          <div className="grid gap-3 sm:grid-cols-2">
            {TRACKS.map((t) => (
              <label
                key={t.value}
                htmlFor={`track-${t.value}`}
                className="flex min-h-[52px] cursor-pointer items-center gap-3 rounded-sm border border-border bg-secondary/50 px-4 py-3 text-[0.9375rem] text-foreground"
              >
                <Checkbox
                  id={`track-${t.value}`}
                  aria-label={t.label}
                  checked={chosenTracks.includes(t.value)}
                  onCheckedChange={() => toggleTrack(t.value)}
                />
                <span className="min-w-0">{t.label}</span>
              </label>
            ))}
          </div>
          <p className="t-meta">Pick one or both.</p>
        </div>


        <div className="space-y-2">
          <Label htmlFor="t1d_connection">What is your primary relationship to Type 1 Diabetes?</Label>
          <select
            id="t1d_connection"
            name="t1d_connection"
            defaultValue={
              T1D_RELATIONSHIPS.includes(
                (profile?.t1d_connection ?? "") as (typeof T1D_RELATIONSHIPS)[number],
              )
                ? (profile?.t1d_connection as string)
                : ""
            }
            className="h-11 w-full rounded-sm border border-border bg-background px-3 text-[0.9375rem] text-foreground"
          >
            <option value="">Select one</option>
            {T1D_RELATIONSHIPS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {profile?.t1d_connection &&
          !T1D_RELATIONSHIPS.includes(
            profile.t1d_connection as (typeof T1D_RELATIONSHIPS)[number],
          ) ? (
            <p className="t-meta">Previously on file: {profile.t1d_connection}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="shirt_size">Shirt size</Label>
          <select
            id="shirt_size"
            name="shirt_size"
            defaultValue={profile?.shirt_size ?? ""}
            className="h-11 w-full rounded-sm border border-border bg-background px-3 text-[0.9375rem] text-foreground"
          >
            <option value="">Select one</option>
            {SHIRT_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <p className="t-meta">
            Used to prefill event signups. Choose I already have a shirt if you do not need a new one.
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label>Programs you want to work on</Label>
            <button
              type="button"
              onClick={() =>
                setSelected(
                  allProgramsSelected ? [] : (programs ?? []).map((program) => program.name),
                )
              }
              className="text-[0.8125rem] font-semibold text-cyan underline-offset-4 hover:underline"
            >
              {allProgramsSelected ? "Deselect all" : "Select all"}
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {(programs ?? []).map((program) => (
              <label
                key={program.id}
                htmlFor={`program-${program.id}`}
                className="flex min-h-[52px] cursor-pointer items-center gap-3 rounded-sm border border-border bg-secondary/50 px-4 py-3 text-[0.9375rem] text-foreground"
              >
                <Checkbox
                  id={`program-${program.id}`}
                  aria-label={program.name}
                  checked={chosenPrograms.includes(program.name)}
                  onCheckedChange={() => toggleProgram(program.name)}
                />
                <span className="min-w-0">{program.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="availability">Availability</Label>
          <Input
            id="availability"
            name="availability"
            defaultValue={profile?.availability ?? ""}
            placeholder="Weeknights, weekend mornings, about an hour a week"
          />
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="signature_title">Signature title</Label>
            <Input
              id="signature_title"
              value={signatureTitle}
              onChange={(event) => setSignatureTitle(event.target.value)}
              placeholder="Community outreach volunteer"
            />
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label htmlFor="email_signature">Email signature</Label>
              <Button type="button" size="sm" variant="outline" onClick={prefillSignature}>
                Build from my profile
              </Button>
            </div>
            <Textarea
              id="email_signature"
              rows={6}
              value={signature}
              onChange={(event) => setSignature(event.target.value)}
              placeholder={"Rich Krummen\nCommunity outreach volunteer\nT1D Warriors\n513-708-3745"}
            />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={includeLogo}
                onCheckedChange={(value) => setIncludeLogo(value === true)}
                aria-label="Include the T1D Warriors logo in my signature"
              />
              <span>Include the T1D Warriors logo under my signature</span>
            </label>
            <p className="t-meta">
              This is added to the bottom of every outreach email you send from the inbox.
            </p>
          </div>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="why_help">Why you want to help</Label>
          <Textarea id="why_help" name="why_help" rows={4} defaultValue={profile?.why_help ?? ""} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="skills">Skills (comma separated)</Label>
          <Input id="skills" name="skills" defaultValue={(profile?.skills ?? []).join(", ")} />
        </div>

        <div className="space-y-2 border-t border-border pt-5">
          <Label>Communication preferences</Label>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-[4px] border border-border bg-muted/40 p-3">
              <p className="text-sm font-medium">Text messages</p>
              <p className="t-meta">{consentSummary(profile?.sms_consent, profile?.sms_consent_at, profile?.sms_consent_source)}</p>
            </div>
            <div className="rounded-[4px] border border-border bg-muted/40 p-3">
              <p className="text-sm font-medium">Email updates</p>
              <p className="t-meta">{consentSummary(profile?.email_consent, profile?.email_consent_at, profile?.email_consent_source)}</p>
            </div>
          </div>
          <p className="t-meta">
            Event announcements and new volunteer opportunities only go out to people with email or
            text turned on. With both off, you will only get messages about shifts you have already
            claimed. To change a preference, contact the volunteer team or use the unsubscribe link
            in any email.
          </p>

        </div>

        <div className="flex items-center justify-between border-t border-border pt-5">

          <p className="t-meta">
            Status: {profile?.status ?? "not set"} · Role: {me?.roles.join(", ") || "none"}
          </p>
          <Button type="submit" disabled={busy} >
            Save profile
          </Button>
        </div>
      </form>
    </AppShell>
  );
}
